import React, {useEffect, useState} from "react";
import {Badge, Button, Card, Col, ProgressBar, Row} from "react-bootstrap";
import {retrieveMemberPackages} from "../../../Api/Members/PackagesFetch";
import {formatDataIt} from "../../../Utils/DateFormat";
import ModalPackage from "../../Modals/Members/ModalPackage";

const STATUS = {
    active:  { label: 'Attivo',   bg: 'success', border: '#198754' },
    future:  { label: 'Prossimo', bg: 'primary',  border: '#0d6efd' },
    expired: { label: 'Scaduto',  bg: 'secondary', border: '#adb5bd' },
}

const getStatus = (pkg, today) => {
    const from = new Date(pkg.valid_from)
    const to   = new Date(pkg.valid_to)
    if (from <= today && today <= to) return 'active'
    if (from > today) return 'future'
    return 'expired'
}

const counter = (value, label, highlight) => (
    <div className={'text-center px-2'} style={{minWidth: '60px'}}>
        <div className={`fw-bold ${highlight ? 'text-success' : ''}`} style={{fontSize: '1.1rem'}}>{value}</div>
        <div className={'text-muted'} style={{fontSize: '0.65rem', whiteSpace: 'nowrap'}}>{label}</div>
    </div>
)

const PackageCard = ({pkg, status, onModifica}) => {
    const s       = STATUS[status]
    const percent = pkg.total_credits > 0 ? Math.round((pkg.remaining / pkg.total_credits) * 100) : 0
    const variant = percent > 50 ? 'success' : percent > 20 ? 'warning' : 'danger'

    return (
        <Card className={'shadow-sm border-0 mb-2'}
              style={{borderLeft: `4px solid ${s.border} !important`, overflow: 'hidden'}}>
            <div style={{borderLeft: `4px solid ${s.border}`}}>
                <Card.Body className={'py-2 px-3'}>
                    <Row className={'align-items-center g-2'}>

                        {/* Sinistra: badge + date + barra */}
                        <Col>
                            <div className={'d-flex align-items-center gap-2 mb-1'}>
                                <Badge bg={s.bg} style={{fontSize: '0.65rem'}}>{s.label}</Badge>
                                <span className={'fw-semibold text-second'} style={{fontSize: '0.85rem'}}>
                                    {formatDataIt(new Date(pkg.valid_from))} – {formatDataIt(new Date(pkg.valid_to))}
                                </span>
                            </div>
                            <div className={'d-flex align-items-center gap-2'}>
                                <ProgressBar
                                    now={percent}
                                    variant={variant}
                                    style={{height: '6px', flex: 1, minWidth: '80px'}}
                                />
                                <small className={'text-muted'} style={{fontSize: '0.7rem', whiteSpace: 'nowrap'}}>
                                    {pkg.remaining}/{pkg.total_credits} rimasti
                                </small>
                            </div>
                        </Col>

                        {/* Centro: 4 contatori */}
                        <Col xs={'auto'} className={'d-flex border-start border-end px-2'}>
                            {counter(pkg.total_credits, 'Totale')}
                            {counter(pkg.consumed,      'Consumati')}
                            {counter(pkg.committed,     'Impegnati')}
                            {counter(pkg.remaining,     'Disponibili', status !== 'expired')}
                        </Col>

                        {/* Destra: azione */}
                        <Col xs={'auto'}>
                            {status !== 'expired'
                                ? <Button size={'sm'} className={'bkg-primary border-0'} style={{fontSize: '0.75rem'}}
                                          onClick={() => onModifica(pkg)}>Modifica</Button>
                                : <span className={'text-muted'} style={{fontSize: '0.75rem'}}>—</span>
                            }
                        </Col>
                    </Row>
                </Card.Body>
            </div>
        </Card>
    )
}

const PackagesTab = ({id, setIsLoading, setMessage, setErrors}) => {
    const [packagesData, setPackagesData] = useState(null)
    const [showModal, setShowModal]       = useState(false)
    const [isRenewal, setIsRenewal]       = useState(false)
    const [selectedPackage, setSelectedPackage] = useState(null)

    const fetchPackages = () => retrieveMemberPackages({id, setPackagesData, setIsLoading, setErrors})

    useEffect(() => { fetchPackages() }, [id])

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const packages       = packagesData?.packages || []
    const inPackageMode  = packagesData?.in_package_mode || false

    const activePackages  = packages.filter(p => getStatus(p, today) === 'active')
    const futurePackages  = packages.filter(p => getStatus(p, today) === 'future')
        .sort((a, b) => new Date(a.valid_from) - new Date(b.valid_from))
    const expiredPackages = packages.filter(p => getStatus(p, today) === 'expired')
        .sort((a, b) => new Date(b.valid_to) - new Date(a.valid_to))

    const openModifica = (pkg) => { setSelectedPackage(pkg); setIsRenewal(false); setShowModal(true) }
    const openRinnova  = ()    => { setSelectedPackage(null); setIsRenewal(true);  setShowModal(true) }

    const SectionLabel = ({text}) => (
        <div className={'d-flex align-items-center gap-2 mt-3 mb-1'}>
            <small className={'text-muted text-uppercase fw-semibold'} style={{fontSize: '0.7rem', letterSpacing: '0.05em'}}>{text}</small>
            <hr className={'flex-grow-1 my-0'}/>
        </div>
    )

    return (
        <>
            <Row className={'mb-3 align-items-center'}>
                <Col className={'d-flex align-items-center gap-2 flex-wrap'}>
                    <h5 className={'mb-0'}>Pacchetto Ingressi Allenamenti</h5>
                    {inPackageMode && <Badge bg={'primary'} style={{fontSize: '0.7rem'}}>Modalità Pacchetti</Badge>}
                </Col>
                <Col className={'text-end'}>
                    <Button size={'sm'} className={'bkg-second border-0'} onClick={openRinnova}>
                        {packages.length === 0 ? 'Aggiungi Pacchetto' : 'Rinnova Pacchetto'}
                    </Button>
                </Col>
            </Row>

            {packages.length === 0 && <p className={'text-muted small'}>Nessun pacchetto configurato.</p>}

            {activePackages.map(pkg  => <PackageCard key={pkg.id} pkg={pkg} status={'active'}  onModifica={openModifica}/>)}

            {futurePackages.length > 0 && <>
                <SectionLabel text={'Prossimi'}/>
                {futurePackages.map(pkg  => <PackageCard key={pkg.id} pkg={pkg} status={'future'}  onModifica={openModifica}/>)}
            </>}

            {expiredPackages.length > 0 && <>
                <SectionLabel text={'Scaduti'}/>
                {expiredPackages.map(pkg => <PackageCard key={pkg.id} pkg={pkg} status={'expired'} onModifica={openModifica}/>)}
            </>}

            <ModalPackage
                show={showModal}
                handleClose={() => setShowModal(false)}
                id={id}
                packageToEdit={selectedPackage}
                isRenewal={isRenewal}
                onSaveSuccess={fetchPackages}
                setMessage={setMessage}
                setIsLoading={setIsLoading}
            />
        </>
    )
}

export default PackagesTab
